import { CopyRightText } from "@/components/custom/CopyRightText"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { Label } from "@/components/ui/label"
import { useEffect, useState } from "react"
import { TCSLink } from "@/components/custom/Link/TCSLink"
import { useLocation, useNavigate } from "react-router-dom"
import { Auth } from "@/API/Auth"
import { AuthSchema } from "@/constants/auth/auth"
import { AuthMessage } from "@/constants/auth/auth"
import { ResponseMessageBox } from "@/components/custom/ResponseMessageBox/ResponseMessageBox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Card,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  PasswordResetTokenBody,
  ResetPasswordConfirmForm,
  ResetPasswordEmailVerificationForm
} from "@/types/auth"

const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

export const ResetPassword = () => {
  const navigate = useNavigate()
  const query = useQuery()
  const resetToken = query.get('token')
  const [preFormMessage, setPreFormMessage] = useState<string>('')
  const [postFormMessage, setPostFormMessage] = useState<string>('')
  const [isEmailVerified, setEmailVerified] = useState(false)

  const resetPasswordEmailVerificationForm = useForm<ResetPasswordEmailVerificationForm>({
    resolver: zodResolver(AuthSchema.resetPasswordEmailVerification),
    defaultValues: {
      email: ""
    },
  })

  const resetPasswordConfirmForm = useForm<ResetPasswordConfirmForm>({
    resolver: zodResolver(AuthSchema.resetPasswordConfirm),
    defaultValues: {
      newPassword: "",
      confirmNewPassword: "",
    },
  })

  const onEmailSubmit = (): void => {
    setEmailVerified(true)
  }

  const onPasswordResetSubmit = async (
    values: ResetPasswordConfirmForm
  ): Promise<void> => {
    if (resetToken?.length) {
      if (values.newPassword !== values.confirmNewPassword) {
        resetPasswordConfirmForm.setError('newPassword', { message: 'Passwords do not match.' })
        resetPasswordConfirmForm.setError('confirmNewPassword', { message: 'Passwords do not match.' })
        return
      }
      const data = {
        email: resetPasswordEmailVerificationForm.getValues('email'),
        token: resetToken,
        newPassword: values.newPassword
      }
      const response = await Auth.resetPassword(data)

      if (!response.data && response.err) {
        setPostFormMessage(response.err)
      } else {
        resetPasswordEmailVerificationForm.reset({ email: "" })
        resetPasswordConfirmForm.reset({
          newPassword: "",
          confirmNewPassword: "",
        })
        setPostFormMessage('')
        setPreFormMessage('')
        navigate('/login?reset=success')
      }
    }
  }

  useEffect(() => {
    setPreFormMessage('')
    setPostFormMessage('')
    resetPasswordEmailVerificationForm.clearErrors()
    resetPasswordConfirmForm.clearErrors()
  }, [resetPasswordEmailVerificationForm, resetPasswordConfirmForm])

  useEffect(() => {
    if (resetToken) {
      const validateResetToken = async (values: PasswordResetTokenBody) => {
        const response = await Auth.validatePasswordResetToken(values)
        if (!response.data && response.err) {
          if (response.err === AuthMessage.INVALID_TOKEN as string) {
            setPreFormMessage(AuthMessage.INVALID_TOKEN)
          } else if (response.err === AuthMessage.TOKEN_EXPIRED as string) {
            setPreFormMessage(AuthMessage.TOKEN_EXPIRED)
          } else {
            setPreFormMessage(AuthMessage.PASSWORD_RESET_TOKEN_VERIFICATION_FAILED)
          }
        }
      }

      validateResetToken({ token: resetToken })
        .then(() => { console.log('checked reset token validity') })
        .catch(() => { console.log('error on checking reset token validity') })
    } else {
      // no token means invalid token
      setPreFormMessage(AuthMessage.INVALID_TOKEN)
    }
  }, [resetToken, navigate])

  return (
    <div className="bg-opacity-50 h-screen py-20 flex flex-col justify-center items-center">
      <Card className="auth-card w-sm flex-column p-10">
        <CardHeader className="mb-12">
          <CardTitle>The Camp Stack</CardTitle>
        </CardHeader>

        {/* Response Message */}
        {(postFormMessage || preFormMessage) && (
          <ResponseMessageBox messageTitle={postFormMessage || preFormMessage} />
        )}

        {/* Before Email Validation */}
        {!isEmailVerified && preFormMessage.length === 0 ? (
          <>
            <Label className="flex justify-start font-semibold text-primary text-xl mb-1">
              Reset Password
            </Label>
            <p className="text-left text-secondary mb-6 leading-5">
              Please verify your email address to reset your password.
            </p>
            <Form {...resetPasswordEmailVerificationForm}>
              <form onSubmit={resetPasswordEmailVerificationForm.handleSubmit(onEmailSubmit)} className="space-y-8">
                <FormField
                  control={resetPasswordEmailVerificationForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel
                        className="flex justify-start mb-2 font-semibold text-primary"
                        isRequired
                      >
                        Email Address
                      </FormLabel>
                      <FormControl>
                        <Input
                          hasError={!!resetPasswordEmailVerificationForm.formState.errors.email}
                          type="email"
                          placeholder="john@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage
                        showIcon={true}
                        className="flex justify-start"
                      />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full mb-2 font-semibold"
                >
                  Continue
                </Button>
              </form>
            </Form>
          </>
        ) : isEmailVerified && preFormMessage.length === 0 ? (
          <Form {...resetPasswordConfirmForm}>
            <form onSubmit={resetPasswordConfirmForm.handleSubmit(onPasswordResetSubmit)} className="space-y-8">
              <FormField
                control={resetPasswordConfirmForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel
                      className="flex justify-start mb-2 font-semibold text-primary"
                      isRequired
                    >
                      New Password
                    </FormLabel>
                    <FormControl>
                      <Input
                        hasError={!!resetPasswordConfirmForm.formState.errors.newPassword}
                        type="password"
                        placeholder="********"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage
                      showIcon={true}
                      className="flex justify-start"
                    />
                  </FormItem>
                )}
              />
              <FormField
                control={resetPasswordConfirmForm.control}
                name="confirmNewPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel
                      className="flex justify-start mb-2 font-semibold text-primary"
                      isRequired
                    >
                      Confirm New Password
                    </FormLabel>
                    <FormControl>
                      <Input
                        hasError={!!resetPasswordConfirmForm.formState.errors.confirmNewPassword}
                        type="password"
                        placeholder="********"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage
                      showIcon={true}
                      className="flex justify-start"
                    />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full mb-2 font-semibold">
                Reset Password
              </Button>
            </form>
          </Form>
        ) : null}
        <CardFooter className="pt-3 flex justify-center">
          <TCSLink
            linkTo='/login'
            className="mt-3 flex justify-start items-center"
            linkText="Back to Log In"
          />
        </CardFooter>
      </Card>
      <CopyRightText />
    </div>
  )
}
